import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import LoanDetail from "./LoanDetail";
import cookies from "../../../../../CommonFunction/cookies";
import { usePagination, useTable } from "react-table";
import DataTable from "../../Elements/DataTable";
import Pagination from "../../Elements/Pagination";
import EmptyDataAlert from "../../Elements/EmptyDataAlert";
import UserProfile from "../CustomerSearch/UserProfile";
import { fetchLoanList } from "../../../../../API/Bank/LoanSearch";
import Loader from "../../../../../CommonFunction/Loader";
import { UserContext } from "../../../../../App";
import { useLocation } from "react-router-dom";

const BankLoanSearch = () => {
  const jwtToken = cookies.get("jwt");
  const decodedData = useContext(UserContext);
  const bankID = decodedData?.bank;
  const branchID = decodedData?.branch;
  const role = decodedData?.role;
  const [loanData, setLoanData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [ucid, setUcid] = useState();
  const [showLoanDetails, setShowLoanDetails] = useState(false);
  const [isExport, setIsExport] = useState(true);
  const [loanId, setLoanId] = useState();
  const [error, setError] = useState(false);
  const [pagination, setPagination] = useState({
    totalDocs: 0,
    totalPages: 1,
    page: 0,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const location = useLocation();
  const query = location?.state?.id;

  const fetchData = useCallback(
    async (page = 1, query = searchQuery) => {
      setLoading(true);
      try {
        const data = await fetchLoanList(
          !isExport,
          page,
          query,
          jwtToken,
          bankID,
          branchID,
          role
        );
        if (data?.docs) {
          setLoanData(data.docs);
          setPagination({
            totalDocs: data.totalDocs,
            totalPages: data.totalPages,
            page: data.page,
            limit: data.limit,
            hasNextPage: data.hasNextPage,
            hasPrevPage: data.hasPrevPage,
          });
          setError(false);
        } else {
          setLoanData([]);
          setError(true);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    },
    [jwtToken]
  );

    // useEffect(() => {
    //   fetchData();
    // }, [fetchData]);

  const handleUciDetail = (uci) => {
    setUcid(uci);
    setShowLoanDetails(true);
  };

  const handleLoanDetails = (loanId) => {
    setLoanId(loanId);
    setShowLoanDetails(true);
  };

  const handleCloseDetail = () => {
    setLoanId(null);
    setUcid(null);
    setShowLoanDetails(false);
  };

  const handleSearch = async () => {
    setLoading(true);
    fetchData(1, searchQuery);
  };

  useEffect(() => {
    if (query) {
      fetchData(query);
      setSearchQuery(query);
    } else {
      fetchData();
    }
  }, []);

  const columns = useMemo(
    () => [
      {
        Header: "Linked Loan",
        accessor: "identity_number",
        Cell: ({ row, value }) => (
          <div className="my-1 px-2">
            <button onClick={() => handleLoanDetails(row.original._id)}>
              <p>{value}</p>
            </button>
          </div>
        ),
      },
      {
        Header: "Borrower Name",
        accessor: "positions",
        Cell: ({ row }) => (
          <div className="text-left border-bgclr2 p-3">
            {row?.original?.positions?.map((position, index) => {
              // Determine if this is a user or an organization based on available properties
              const entity = position?.user
                ? position?.user
                : position?.organization;
              const name = entity?.first_name
                ? `${entity?.first_name} ${entity?.last_name}`
                : entity?.name;
              return (
                <div key={index} style={{ height: "40px" }}>
                  <div>
                    <div className="p-[2px]">
                      {name
                        ? name.charAt(0).toUpperCase() + name.slice(1)
                        : "Null"}{" "}
                      <sup
                        data-tooltip-target="tooltip-right"
                        data-tooltip-placement="right"
                        className={`border-[1.5px] rounded-md px-1.5 py-0.5 text-[15px] ml-2 mt-1 font-semibold ${
                          position.role
                            ? position.role === "borrower"
                              ? "border-red-700 text-red-600"
                              : position.role === "mortgagor"
                              ? "border-blue-700 text-blue-800"
                              : position.role === "guarantor"
                              ? "border-green-700 text-green-800"
                              : "border-gray-700 text-gray-800"
                            : ""
                        }`}
                      >
                        {/* <Tooltip message={position?.role}> */}
                        {position.role
                          ? position.role.charAt(0).toUpperCase()
                          : ""}
                        {/* </Tooltip> */}
                      </sup>
                      {/* <div id="tooltip-right" role="tooltip" className="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium text-white transition-opacity duration-300 bg-gray-900 rounded-lg shadow-sm opacity-0 tooltip dark:bg-gray-700">
                        {position.role}
                        <div className="tooltip-arrow" data-popper-arrow></div>
                      </div> */}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ),
      },

      {
        Header: "UCI",
        accessor: "system_id", // Change accessor to a unique value
        Cell: ({ row }) => (
          <div className="text-center">
            {row?.original?.positions?.map((position) => (
              <div className="border-bgclr2 my-4 px-2">
                <button
                  onClick={() =>
                    handleUciDetail(
                      position.user
                        ? position?.user?.system_id
                        : position?.organization?.system_id
                    )
                  }
                >
                  {position.user
                    ? position?.user?.system_id
                    : position?.organization?.system_id || "Null"}
                </button>
              </div>
            ))}
          </div>
        ),
      },

      {
        Header: "Loan Amount",
        accessor: "amount",
        Cell: ({ value }) => (
          <div className="text-center  border-bgclr2">
            <div className="px-2 border-bgclr2 my-3">{value}</div>
          </div>
        ),
      },
      {
        Header: "Branch",
        accessor: "branch",
        Cell: ({ value }) => (
          <div className="text-center  border-bgclr2">
            <div className="px-2 border-bgclr2 my-3">{value?.name}</div>
          </div>
        ),
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }) => (
          <div className="text-center  border-bgclr2">
            <div className="px-2 border-bgclr2 my-3">{value}</div>
          </div>
        ),
      },
      // {
      //   Header: "Person In Charge",
      //   accessor: "person_in_charge?.name",
      //   Cell: ({ value }) => (
      //     <div className="text-center  border-bgclr2">
      //       <div className="px-2 border-bgclr2 my-3">
      //         {value ? value.charAt(0).toUpperCase() + value.slice(1) : "Null"}
      //       </div>
      //     </div>
      //   ),
      // },
    ],
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, page, prepareRow } =
    useTable(
      {
        columns,
        data: loanData,
        initialState: { pageIndex: 0 },
        manualPagination: true,
      },
      usePagination
    );

  const nextPage = useCallback(() => {
    if (pagination.hasNextPage) fetchData(pagination.page + 1, searchQuery);
  }, [fetchData, searchQuery, pagination]);

  const previousPage = useCallback(() => {
    if (pagination.hasPrevPage) fetchData(pagination.page - 1, searchQuery);
  }, [fetchData, searchQuery, pagination]);

  return (
    <div className="root-style">
      {!showLoanDetails ? (
        <div>
          <div className="searchBar-style">
            <input
              type="search"
              placeholder="Search by loan number"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="searchBar-input"
              onInput={(e) => {
                e.target.value = e.target.value.replace(/[^0-9]/g, "");
              }}
            />
            <button onClick={() => handleSearch()} className="searchBar-btn">
              Search
            </button>
          </div>
          {loading ? (
            <div>
              <Loader />
            </div>
          ) : (
            <>
              {loanData.length === 0 ? (
                <div className="mt-10 h-screen">
                  <EmptyDataAlert
                    title={
                      error ? "No Loans Found" : "Search Loan by Loan number"
                    }
                  />
                </div>
              ) : (
                <div className="mt-6">
                  <div>
                    <div className="pt-3 overflow-x-auto">
                      <DataTable
                        getTableProps={getTableProps}
                        headerGroups={headerGroups}
                        getTableBodyProps={getTableBodyProps}
                        page={page}
                        prepareRow={prepareRow}
                      />
                    </div>
                    <Pagination
                      previousPage={previousPage}
                      nextPage={nextPage}
                      canPreviousPage={pagination.hasPrevPage}
                      canNextPage={pagination.hasNextPage}
                      pagination={{
                        page: pagination.page,
                        totalPages: pagination.totalPages,
                      }}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <div>
          {loanId && (
            <div>
              <LoanDetail onClose={handleCloseDetail} loanId={loanId} />
            </div>
          )}
          {ucid && (
            <div>
              <UserProfile onClose={handleCloseDetail} ucid={ucid} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BankLoanSearch;
